import { useRef, useState } from "react";
import { errorDetail } from "../helper/errorDetail";

const EMPTY_OUTCOME = { message: "", severity: "error" };

function readWarning(storageKey) {
  try {
    const stored = JSON.parse(sessionStorage.getItem(storageKey));
    if (stored?.severity === "warning" && typeof stored.message === "string") return stored;
  } catch {
    // Storage is optional; failures still render in the current session.
  }
  return EMPTY_OUTCOME;
}

export default function usePasswordOutcome(storageKey) {
  const [outcome, setOutcome] = useState(() => readWarning(storageKey));
  const [submitting, setSubmitting] = useState(false);
  const inFlight = useRef(false);

  const runRequest = async (request) => {
    if (inFlight.current) return false;
    inFlight.current = true;
    setSubmitting(true);
    setOutcome(EMPTY_OUTCOME);
    try { sessionStorage.removeItem(storageKey); } catch { /* Optional storage. */ }
    let response;
    try {
      response = await request();
    } catch {
      response = undefined;
    } finally {
      inFlight.current = false;
      setSubmitting(false);
    }
    if (response?.status === 200) return true;

    const next = response?.status === 429
      ? { severity: "warning", message: errorDetail(response.data?.detail,
        "Too many password update requests. Please try again later.") }
      : { severity: "error", message: response === undefined
        ? "We could not reach the server. Check your connection and try again."
        : response?.status >= 500
          ? "The server could not update your password. Please try again later."
          : errorDetail(response?.data?.detail, "Unable to update password. Please try again.") };
    setOutcome(next);
    if (next.severity === "warning") {
      try { sessionStorage.setItem(storageKey, JSON.stringify(next)); } catch { /* Optional storage. */ }
    }
    return false;
  };

  return { outcome, submitting, runRequest };
}
